import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { chatbotAdminApi } from '@/features/chatbot/api/chatbotApi';
import { getPlatformAdminErrorMessage } from '@/features/platform-admin/api/platformAdminApi';
import type { TenantEmailAccountPublic, TenantEmailProvider } from '@workspace/types';

type TenantEmailCardProps = {
  tenantId: string;
};

type EditorState = {
  accountId?: string;
  provider: TenantEmailProvider;
  fromAddress: string;
  apiKey: string;
  testTo: string;
};

const providerLabel: Record<TenantEmailProvider, string> = {
  BREVO: 'Brevo',
  SENDGRID: 'SendGrid',
};

function statusSummary(accounts: TenantEmailAccountPublic[]): string {
  if (accounts.length === 0) {
    return 'Todavía no hay correo guardado para este tenant. Usá la tarjeta de abajo; el botón Guardar de arriba no guarda la clave ni la dirección.';
  }
  const ready = accounts.filter(account => account.ready).length;
  const pending = accounts.length - ready;
  if (ready === accounts.length) {
    return `${ready === 1 ? 'Una cuenta lista' : `${ready} cuentas listas`}. Podés probar de nuevo cuando quieras.`;
  }
  if (ready === 0) {
    return `${accounts.length === 1 ? 'Hay una cuenta guardada' : `Hay ${accounts.length} cuentas guardadas`} pero ninguna está lista. Probá con tu correo.`;
  }
  return `${ready} lista(s), ${pending} sin comprobar.`;
}

export function TenantEmailCard({ tenantId }: TenantEmailCardProps) {
  const [accounts, setAccounts] = useState<TenantEmailAccountPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [testToByAccount, setTestToByAccount] = useState<Record<string, string>>({});

  const summary = useMemo(() => statusSummary(accounts), [accounts]);

  const load = async () => {
    try {
      setLoading(true);
      const listed = await chatbotAdminApi.listTenantEmailAccounts(tenantId);
      setAccounts(listed);
    } catch (error) {
      setRowErrors({ _load: getPlatformAdminErrorMessage(error, 'No se pudieron cargar las cuentas') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [tenantId]);

  const openNew = () => {
    setEditor({
      provider: 'BREVO',
      fromAddress: '',
      apiKey: '',
      testTo: '',
    });
    setRowErrors({});
  };

  const openEdit = (account: TenantEmailAccountPublic) => {
    setEditor({
      accountId: account.id,
      provider: account.provider,
      fromAddress: account.fromAddress,
      apiKey: '',
      testTo: testToByAccount[account.id] ?? '',
    });
    setRowErrors(prev => ({ ...prev, [account.id]: '' }));
  };

  const persistEditor = async (): Promise<string | null> => {
    if (!editor) {
      return null;
    }
    if (!editor.fromAddress.trim()) {
      setRowErrors({ _form: 'Escribí la dirección.' });
      return null;
    }
    if (editor.accountId) {
      if (!editor.apiKey.trim()) {
        await chatbotAdminApi.updateTenantEmailAccount(
          editor.accountId,
          {
            provider: editor.provider,
            fromAddress: editor.fromAddress,
          },
          tenantId,
        );
        return editor.accountId;
      }
      await chatbotAdminApi.updateTenantEmailAccount(
        editor.accountId,
        {
          provider: editor.provider,
          fromAddress: editor.fromAddress,
          apiKey: editor.apiKey.trim(),
        },
        tenantId,
      );
      return editor.accountId;
    }
    if (!editor.apiKey.trim()) {
      setRowErrors({ _form: 'Falta la clave.' });
      return null;
    }
    const created = await chatbotAdminApi.createTenantEmailAccount(
      {
        provider: editor.provider,
        fromAddress: editor.fromAddress,
        apiKey: editor.apiKey.trim(),
      },
      tenantId,
    );
    return created.id;
  };

  const saveCredentialsOnly = async () => {
    if (!editor) {
      return;
    }
    setSaving(true);
    try {
      const id = await persistEditor();
      if (!id) {
        return;
      }
      setEditor(null);
      setRowErrors({});
      await load();
    } catch (error) {
      setRowErrors({ _form: getPlatformAdminErrorMessage(error, 'No se pudieron guardar las credenciales') });
    } finally {
      setSaving(false);
    }
  };

  const runTestFlow = async () => {
    if (!editor) {
      return;
    }
    if (!editor.testTo.trim()) {
      setRowErrors({ _form: 'Escribí tu correo para la prueba.' });
      return;
    }
    setSaving(true);
    const accountId = editor.accountId;
    try {
      const targetId = (await persistEditor()) ?? accountId;
      if (!targetId) {
        return;
      }
      await chatbotAdminApi.testTenantEmailAccount(targetId, { to: editor.testTo.trim() }, tenantId);
      setEditor(null);
      setRowErrors({});
      await load();
    } catch (error) {
      const message = getPlatformAdminErrorMessage(error, 'No se pudo probar el correo');
      if (accountId) {
        setRowErrors(prev => ({ ...prev, [accountId]: message }));
      } else {
        setRowErrors({ _form: message });
      }
      await load();
    } finally {
      setSaving(false);
    }
  };

  const testSavedAccount = async (account: TenantEmailAccountPublic) => {
    const to = (testToByAccount[account.id] ?? '').trim();
    if (!to) {
      setRowErrors(prev => ({ ...prev, [account.id]: 'Escribí tu correo.' }));
      return;
    }
    setSaving(true);
    try {
      await chatbotAdminApi.testTenantEmailAccount(account.id, { to }, tenantId);
      setRowErrors(prev => ({ ...prev, [account.id]: '' }));
      await load();
    } catch (error) {
      setRowErrors(prev => ({
        ...prev,
        [account.id]: getPlatformAdminErrorMessage(error, 'No se pudo probar el correo'),
      }));
    } finally {
      setSaving(false);
    }
  };

  const keyHint =
    editor?.provider === 'BREVO'
      ? 'La copiás en Brevo, en SMTP y API.'
      : 'La copiás en SendGrid, en Settings, API Keys.';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Correo</CardTitle>
        <CardDescription>
          Podés tener más de una. Cada instancia elige cuál usa. {summary}
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        {loading ? <p className='text-sm text-muted-foreground'>Cargando…</p> : null}
        {rowErrors._load ? <p className='text-sm text-destructive'>{rowErrors._load}</p> : null}

        {!loading && accounts.length === 0 && !editor ? (
          <p className='text-sm text-muted-foreground rounded-md border border-dashed p-3'>
            Sin cuentas guardadas. Agregá Brevo o SendGrid acá; no se guarda con el botón Guardar del formulario del tenant.
          </p>
        ) : null}

        {accounts.map(account => (
          <div key={account.id} className='rounded-lg border p-3 space-y-3'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='text-sm font-medium'>
                {account.ready ? 'Listo' : 'Falta comprobar que llega'}
                <span className='text-muted-foreground font-normal'> · {account.fromAddress}</span>
              </div>
              <Button type='button' variant='outline' size='sm' disabled={saving} onClick={() => openEdit(account)}>
                Cambiar
              </Button>
            </div>
            <dl className='grid gap-1 text-sm text-muted-foreground'>
              <div className='flex gap-2'>
                <dt className='shrink-0'>Proveedor:</dt>
                <dd>{providerLabel[account.provider]}</dd>
              </div>
              <div className='flex gap-2'>
                <dt className='shrink-0'>Dirección:</dt>
                <dd>{account.fromAddress}</dd>
              </div>
              <div className='flex gap-2'>
                <dt className='shrink-0'>Clave:</dt>
                <dd>
                  {account.configured
                    ? account.apiKeyLast4
                      ? `Guardada · …${account.apiKeyLast4}`
                      : 'Guardada'
                    : 'Sin guardar'}
                </dd>
              </div>
            </dl>
            {rowErrors[account.id] ? (
              <p className='text-sm text-destructive'>{rowErrors[account.id]}</p>
            ) : null}
            {account.configured ? (
              <div className='flex flex-wrap items-end gap-2 border-t pt-3'>
                <div className='flex-1 min-w-[200px]'>
                  <Label htmlFor={`retest-${account.id}`}>Tu correo</Label>
                  <Input
                    id={`retest-${account.id}`}
                    type='email'
                    autoComplete='email'
                    maxLength={255}
                    placeholder='Tu correo'
                    value={testToByAccount[account.id] ?? ''}
                    disabled={saving}
                    onChange={event =>
                      setTestToByAccount(prev => ({
                        ...prev,
                        [account.id]: event.target.value.slice(0, 255),
                      }))
                    }
                  />
                </div>
                <Button
                  type='button'
                  variant='secondary'
                  size='sm'
                  disabled={saving}
                  onClick={() => void testSavedAccount(account)}
                >
                  {account.ready ? 'Probar de nuevo' : 'Probar con mi correo'}
                </Button>
              </div>
            ) : null}
          </div>
        ))}

        {!editor ? (
          <Button type='button' variant={accounts.length ? 'outline' : 'default'} onClick={openNew}>
            {accounts.length ? 'Agregar otro correo' : 'Agregar correo'}
          </Button>
        ) : (
          <div className='space-y-3 rounded-lg border p-3'>
            <p className='text-sm text-muted-foreground'>
              {editor.accountId ? 'Cambiá la cuenta o probá que llega.' : 'Nueva cuenta para este tenant.'}
            </p>
            <div className='flex gap-2'>
              {(['BREVO', 'SENDGRID'] as TenantEmailProvider[]).map(option => (
                <Button
                  key={option}
                  type='button'
                  variant={editor.provider === option ? 'default' : 'outline'}
                  onClick={() => setEditor({ ...editor, provider: option })}
                >
                  {providerLabel[option]}
                </Button>
              ))}
            </div>
            <div className='space-y-2'>
              <Label htmlFor='tenant-email-from'>Dirección que ve la gente</Label>
              <Input
                id='tenant-email-from'
                value={editor.fromAddress}
                maxLength={255}
                autoComplete='off'
                onChange={event => setEditor({ ...editor, fromAddress: event.target.value.slice(0, 255) })}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='tenant-email-key'>
                {editor.provider === 'BREVO' ? 'Clave de Brevo' : 'Clave de SendGrid'}
              </Label>
              <Input
                id='tenant-email-key'
                type='password'
                autoComplete='new-password'
                maxLength={4096}
                value={editor.apiKey}
                placeholder={
                  editor.accountId
                    ? `Clave guardada. Escribí otra para cambiarla.${accounts.find(a => a.id === editor.accountId)?.apiKeyLast4 ? ` …${accounts.find(a => a.id === editor.accountId)?.apiKeyLast4}` : ''}`
                    : ''
                }
                onChange={event => setEditor({ ...editor, apiKey: event.target.value.slice(0, 4096) })}
              />
              <p className='text-xs text-muted-foreground'>{keyHint}</p>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='tenant-email-test-to'>Tu correo</Label>
              <Input
                id='tenant-email-test-to'
                type='email'
                autoComplete='email'
                maxLength={255}
                value={editor.testTo}
                placeholder='Tu correo'
                onChange={event => setEditor({ ...editor, testTo: event.target.value.slice(0, 255) })}
              />
            </div>
            {rowErrors._form ? <p className='text-sm text-destructive'>{rowErrors._form}</p> : null}
            <div className='flex flex-wrap gap-2'>
              <Button type='button' variant='outline' disabled={saving} onClick={() => void saveCredentialsOnly()}>
                {saving ? 'Guardando…' : 'Guardar credenciales'}
              </Button>
              <Button type='button' disabled={saving} onClick={() => void runTestFlow()}>
                {saving ? 'Enviando…' : 'Probar con mi correo'}
              </Button>
              <Button type='button' variant='ghost' disabled={saving} onClick={() => setEditor(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
